import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Stack, ThemeProvider } from '@mui/material';

import {
	useDTicketSubscription,
	useDTicketSubscriptionOffering,
} from '@mopla/business-logic';
import { MOPLA_SOLUTIONS_FAQ_URL } from '@mopla/constants';
import { passengerThemeV2 } from '@mopla/mui';
import { DTicketPageLayout } from '@mopla/ui';

import { usePassengerAppNavigate } from '../../../hooks';

import { getDTicketPageTitleKey } from './utils/utils';
import { usePageLayoutConfig } from './hooks';
//import { getMockDTicketData } from './mock';
import {
	DTicketCardSectionWrapper,
	DTicketCTASection,
	DTicketHelpModal,
	DTicketInfoMessageHeaderSection,
	DTicketLegalSection,
	DTicketLinkHelpSection,
	DTicketOptionsListSection,
} from './sections';
import { UserDTicketLoading } from './UserDTicketLoading';

export const UserDTicket = () => {
	const { t } = useTranslation('userDTicket');
	const {
		subscription,
		tickets,
		isSubscriptionLoading,
		isTicketsLoading,
		hasSubscriptionLoaded,
		hasTicketsLoaded,
		refetchTickets,
		refetchSubscription,
	} = useDTicketSubscription();

	const { navigateToHome, navigateToEditPaymentMethod } =
		usePassengerAppNavigate();
	const { subscriptionOffering } = useDTicketSubscriptionOffering();
	// Note: development needed
	// const { subscription, tickets } = getMockDTicketData("two-invalid-tickets");
	const pageTitleKey = getDTicketPageTitleKey(
		subscription?.type ?? subscriptionOffering?.subscriptionType
	);

	const [isInitialized, setIsInitialized] = useState(false);
	const [isHelpModalOpen, setIsHelpModalOpen] = useState(false);
	const [ctaHeight, setCTAHeight] = useState(0);

	const isRefreshing = isTicketsLoading || isSubscriptionLoading;
	/**
	 * Only show the full-page skeleton on the very first load when no cached
	 * data has ever been available. `hasSubscriptionLoaded` / `hasTicketsLoaded`
	 * are sticky flags — once true they never revert, so subsequent mounts
	 * (and offline launches with cached RxDB data) skip the loading screen
	 * even while a background refetch is in-flight.
	 */
	const isPageLoading =
		(!hasSubscriptionLoaded && isSubscriptionLoading) ||
		(!hasTicketsLoaded && isTicketsLoading);

	const handleRefresh = useCallback(() => {
		refetchTickets();
		refetchSubscription();
	}, [refetchTickets, refetchSubscription]);

	const {
		infoMessage,
		showLegalSection,
		showOptionsSection,
		options,
		showHelpSection,
		cta,
	} = usePageLayoutConfig({
		subscription,
		onRefresh: handleRefresh,
		onEditPaymentMethod: navigateToEditPaymentMethod,
	});

	useEffect(() => {
		refetchTickets();
		refetchSubscription();

		// Wait for data to load before enabling redirect check
		const timer = setTimeout(() => {
			setIsInitialized(true);
		}, 500);

		return () => clearTimeout(timer);
		/** Run once on mount */
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const hasNoData = !subscription && tickets.length === 0;

	// Redirect to home if no subscription and no tickets (after initialization)
	useEffect(() => {
		if (isInitialized && hasNoData) {
			navigateToHome();
		}
	}, [isInitialized, hasNoData, navigateToHome]);

	if (isPageLoading || hasNoData) {
		return <UserDTicketLoading onBack={navigateToHome} />;
	}

	return (
		<ThemeProvider theme={passengerThemeV2}>
			<DTicketPageLayout
				title={t(pageTitleKey)}
				onBack={navigateToHome}
				infoUrl={MOPLA_SOLUTIONS_FAQ_URL}
				scrollable={true}
				dataTestId="user-dticket-page"
			>
				<Stack sx={cta ? { pb: `${ctaHeight}px` } : undefined}>
					{infoMessage && (
						<Stack sx={{ mb: 2 }}>
							<DTicketInfoMessageHeaderSection {...infoMessage} />
						</Stack>
					)}
					<Stack gap={5}>
						<DTicketCardSectionWrapper
							subscription={subscription}
							tickets={tickets}
						/>
						{showLegalSection && (
							<DTicketLegalSection companyName={subscription?.companyName} />
						)}
						{showOptionsSection && (
							<DTicketOptionsListSection options={options} />
						)}
					</Stack>
					{showHelpSection && (
						<Stack alignItems="center" sx={{ mt: 3 }}>
							<DTicketLinkHelpSection
								onClick={() => setIsHelpModalOpen(true)}
							/>
						</Stack>
					)}
				</Stack>
			</DTicketPageLayout>
			<DTicketHelpModal
				open={isHelpModalOpen}
				onClose={() => setIsHelpModalOpen(false)}
			/>
			{cta && (
				<DTicketCTASection
					config={cta}
					isLoading={isRefreshing}
					onHeightChange={setCTAHeight}
				/>
			)}
		</ThemeProvider>
	);
};
